import { Button, Divider, Form, Modal, Select } from "antd";
import dayjs from "dayjs";
import { useIntl } from "react-intl";
import { FormDatePicker } from "~components/Form/FormDatePicker";
import { FormInput } from "~components/Form/FormInput";
import { FormSelect } from "~components/Form/FormSelect";
import timeZones from "~config/timeZones";
import { BroadcastTypes } from "~enums/broadcastTypes.ts";
import type { AddBroadcastForm } from "~pages/Fixtures/BroadcastPage/types/addBroadcastForm.ts";
import type { BroadcastTableRow } from "~pages/Fixtures/BroadcastPage/types/broadcastTableRow.ts";
import type { Broadcast } from "~types/broadcast.ts";
import "./UpsertBroadcastModal.scss";
import { Fragment, useState } from "react";

type Props = {
  isOpen: boolean;
  onCancel: () => void;
  loading: boolean;
  onOk: (data: Broadcast) => Promise<void>;
  broadcast?: BroadcastTableRow;
};

export const UpsertBroadcastModal = ({ isOpen, onOk, onCancel, loading, broadcast }: Props) => {
  const [form] = Form.useForm<AddBroadcastForm>();
  const { formatMessage } = useIntl();

  const fields = [
    {
      titleIntlKey: "fixture.broadcast.broadcaster",
      descriptionIntlKey: "form.help.fixture.broadcast.broadcaster",
    },
    {
      titleIntlKey: "fixture.broadcast.broadcastType",
      descriptionIntlKey: "form.help.fixture.broadcast.broadcastType",
    },
    {
      titleIntlKey: "fixture.broadcast.broadcasterURL",
      descriptionIntlKey: "form.help.fixture.broadcast.broadcasterURL",
    },
    {
      titleIntlKey: "fixture.broadcast.broadcastURL",
      descriptionIntlKey: "form.help.fixture.broadcast.broadcastURL",
    },
    {
      titleIntlKey: "fixture.broadcast.broadcastTimezone",
      descriptionIntlKey: "form.help.fixture.broadcast.broadcastTimezone",
    },
    {
      titleIntlKey: "fixture.broadcast.locale",
      descriptionIntlKey: "form.help.fixture.broadcast.locale",
    },
    {
      titleIntlKey: "fixture.broadcast.startTimeLocal",
      descriptionIntlKey: "form.help.fixture.broadcast.startTimeLocal",
    },
  ];

  const [isHelpSectionOpen, setIsHelpSectionOpen] = useState<boolean>(false);

  const onSave = async () => {
    const dataSource = (await form.validateFields()) ?? {};
    await onOk(dataSource);
    form.resetFields();
  };

  return (
    <>
      {isHelpSectionOpen ? (
        <Modal
          open={isHelpSectionOpen}
          onCancel={() => setIsHelpSectionOpen(false)}
          title={
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <i className="fas fa-tower-broadcast" />
              {formatMessage({
                id: "broadcast.modal.upsert.title",
              })}
            </div>
          }
          okButtonProps={{ loading, hidden: true }}
          cancelButtonProps={{ hidden: true }}
        >
          <span style={{ marginTop: 10, fontWeight: 500 }}>{formatMessage({ id: "broadcast.modal.upsert.description" })}</span>
          <div>{formatMessage({ id: "details" })}</div>
          <Divider style={{ margin: "1px 0", borderColor: "#DADADA" }} />
          {fields.map((field) => (
            <Fragment key={field.titleIntlKey}>
              <div className="help-label">{formatMessage({ id: `${field.titleIntlKey}` })}</div>
              <span className="help-description">{formatMessage({ id: `${field.descriptionIntlKey}` })}</span>
            </Fragment>
          ))}
        </Modal>
      ) : (
        <Modal
          open={isOpen}
          onCancel={onCancel}
          title={
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <i className="fas fa-tower-broadcast" />
              {formatMessage({
                id: "broadcast.modal.upsert.title",
              })}
            </div>
          }
          okButtonProps={{ loading }}
          cancelButtonProps={{ disabled: loading }}
          cancelText={formatMessage({
            id: "cancel",
          })}
          onOk={onSave}
        >
          <span style={{ marginTop: 10, fontWeight: 500 }}>{formatMessage({ id: "broadcast.modal.upsert.description" })}</span>
          <div className="details-container">
            {formatMessage({ id: "details" })}
            <Button
              icon={<i className="fa-regular fa-circle-question" />}
              type="text"
              size="small"
              onClick={() => setIsHelpSectionOpen(true)}
            />
          </div>

          <Divider style={{ margin: "1px 0", borderColor: "#DADADA" }} />
          <Form
            initialValues={{
              ...broadcast,
              startTimeLocal: broadcast?.startTimeLocal ? dayjs(broadcast.startTimeLocal) : null,
            }}
            form={form}
            className={"upsert-broadcast-modal-form"}
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
            style={{ marginTop: "20px" }}
          >
            <div className="columnsContainer">
              <FormInput<AddBroadcastForm>
                label={formatMessage({ id: "fixture.broadcast.broadcaster" })}
                name="broadcaster"
                inputProps={{ placeholder: "XYZ Network" }}
                rules={[
                  {
                    required: true,
                    message: formatMessage(
                      {
                        id: "form.help.required.field",
                      },
                      {
                        field: formatMessage({
                          id: "name.local",
                        }),
                      },
                    ),
                  },
                  {
                    max: 30,
                    message: formatMessage({ id: "form.validation.max.length" }, { length: 30 }),
                  },
                ]}
              />
              <FormSelect<AddBroadcastForm> label={formatMessage({ id: "fixture.broadcast.broadcastType" })} name="broadcastType">
                {Object.values(BroadcastTypes).map((type) => (
                  <Select.Option key={type} value={type}>
                    {type}
                  </Select.Option>
                ))}
              </FormSelect>
            </div>
            <FormInput<AddBroadcastForm>
              label={formatMessage({ id: "fixture.broadcast.broadcasterURL" })}
              name="broadcasterURL"
              inputProps={{ placeholder: "www.XYZNetwork.com" }}
              rules={[
                {
                  max: 255,
                  message: formatMessage({ id: "form.validation.max.length" }, { length: 255 }),
                },
              ]}
            />
            <FormInput<AddBroadcastForm>
              label={formatMessage({ id: "fixture.broadcast.broadcastURL" })}
              inputProps={{ placeholder: "www.XYZNetwork.com/fixture/123" }}
              name="broadcastURL"
              rules={[
                {
                  max: 255,
                  message: formatMessage({ id: "form.validation.max.length" }, { length: 255 }),
                },
              ]}
            />
            <div className="columnsContainer">
              <FormSelect<AddBroadcastForm>
                label={formatMessage({
                  id: "fixture.broadcast.broadcastTimezone",
                })}
                name="broadcastTimezone"
              >
                {Object.values(timeZones).map((timezone) => (
                  <Select.Option key={timezone} value={timezone}>
                    {timezone}
                  </Select.Option>
                ))}
              </FormSelect>
              <FormInput<AddBroadcastForm>
                label={formatMessage({ id: "fixture.broadcast.locale" })}
                inputProps={{ placeholder: "en=EN" }}
                name="locale"
                rules={[
                  {
                    max: 10,
                    message: formatMessage({ id: "form.validation.max.length" }, { length: 10 }),
                  },
                ]}
              />
            </div>
            <div style={{ width: "50%" }} className="columnsContainer">
              <FormDatePicker<AddBroadcastForm>
                label={formatMessage({
                  id: "fixture.broadcast.startTimeLocal",
                })}
                name="startTimeLocal"
                datePickerProps={{
                  format: "DD/MM/YYYY - HH:mm:ss",
                  showTime: true,
                }}
              />
            </div>
          </Form>
        </Modal>
      )}
    </>
  );
};
